import React, { useState, useEffect } from 'react';
import { 
  Download, FileText, TrendingDown, DollarSign, 
  Package, AlertTriangle, BarChart3, Loader 
} from 'lucide-react';
import apiClient from '../../api/client';
import './ReportsPage.css';

function ReportsPage() {
  const [reportType, setReportType] = useState('stock_summary');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const reportTypes = [
    { 
      value: 'stock_summary', 
      label: 'Stock Summary', 
      icon: <Package size={20} />,
      description: 'Overview of all stock levels across warehouses'
    },
    { 
      value: 'low_stock', 
      label: 'Low Stock Alert', 
      icon: <AlertTriangle size={20} />,
      description: 'Products below reorder level'
    },
    { 
      value: 'stock_value', 
      label: 'Stock Value', 
      icon: <DollarSign size={20} />,
      description: 'Total inventory value by warehouse'
    },
    { 
      value: 'warehouse_comparison', 
      label: 'Warehouse Comparison', 
      icon: <BarChart3 size={20} />,
      description: 'Compare stock across all warehouses'
    },
  ];

  useEffect(() => {
    loadReport();
  }, [reportType]);

  const loadReport = async () => {
    setLoading(true);
    setError(null);
    try {
      // ✅ FIXED: Backtick inside parentheses
      const response = await apiClient.get(`/inventory-reports/?type=${reportType}`);
      setReportData(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const downloadCSV = async () => {
    try {
      // ✅ FIXED: Backtick inside parentheses
      const response = await apiClient.get(`/export-report-csv/?type=${reportType}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `inventory_${reportType}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Download error:', err);
      alert('Failed to download report');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-TZ', {
      style: 'currency',
      currency: 'TZS',
      minimumFractionDigits: 0
    }).format(value);
  };

  const renderReport = () => {
    if (loading) {
      return (
        <div className="loading-container">
          <Loader size={48} className="spin" />
          <p>Loading report...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="error-container">
          <AlertTriangle size={48} />
          <p>{error}</p>
          <button onClick={loadReport} className="btn-retry">
            Try Again
          </button>
        </div>
      );
    }

    if (!reportData) {
      return null;
    }

    switch (reportType) {
      case 'stock_summary':
        return <StockSummaryReport data={reportData} formatCurrency={formatCurrency} />;
      case 'low_stock':
        return <LowStockReport data={reportData} />;
      case 'stock_value':
        return <StockValueReport data={reportData} formatCurrency={formatCurrency} />;
      case 'warehouse_comparison':
        return <WarehouseComparisonReport data={reportData} formatCurrency={formatCurrency} />;
      default:
        return null;
    }
  };

  return (
    <div className="reports-page">
      <div className="reports-header">
        <div className="header-left">
          <FileText size={32} />
          <div>
            <h1>Inventory Reports</h1>
            <p>Generate and export detailed inventory reports</p>
          </div>
        </div>
        <button onClick={downloadCSV} className="btn-download" disabled={loading || !reportData}>
          <Download size={20} />
          Export CSV
        </button>
      </div>

      <div className="report-selector">
        {reportTypes.map((type) => (
          <button
            key={type.value}
            onClick={() => setReportType(type.value)}
            className={`report-type-btn ${reportType === type.value ? 'active' : ''}`}  // ✅ FIXED: Curly braces around backtick
          >
            {type.icon}
            <div className="report-type-info">
              <span className="report-type-label">{type.label}</span>
              <span className="report-type-desc">{type.description}</span>
            </div>
          </button>
        ))}
      </div>

      <div className="report-content">
        {renderReport()}
      </div>
    </div>
  );
}


// ⭐ Stock Summary Report Component - WITH SAFETY
function StockSummaryReport({ data, formatCurrency }) {
  const summary = data?.summary || {};
  const items = data?.data || [];

  return (
    <div className="report-section">
      <div className="report-summary-cards">
        <div className="summary-card">
          <Package size={24} />
          <div>
            <span className="card-label">Total Products</span>
            <span className="card-value">{summary.total_products || 0}</span>
          </div>
        </div>
        <div className="summary-card">
          <DollarSign size={24} />
          <div>
            <span className="card-label">Total Value</span>
            <span className="card-value">{formatCurrency(summary.total_value || 0)}</span>
          </div>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="no-data-message">
          <Package size={48} />
          <p>No stock data available</p>
        </div>
      ) : (
        <div className="report-table-container">
          <table className="report-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>SKU</th>
                <th>Variant</th>
                <th>Total Qty</th>
                <th>Cost Price</th>
                <th>Selling Price</th>
                <th>Total Value</th>
                <th>Warehouses</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => (
                <tr key={idx}>
                  <td className="product-name">{item.product_name}</td>
                  <td className="sku">{item.product_sku}</td>
                  <td>{item.variant || '-'}</td>
                  <td className="quantity">{item.total_quantity}</td>
                  <td>{formatCurrency(item.cost_price)}</td>
                  <td>{formatCurrency(item.selling_price)}</td>
                  <td className="total-value">{formatCurrency(item.total_value)}</td>
                  <td>
                    <div className="warehouse-list">
                      {(item.warehouses || []).map((wh, i) => (
                        <div key={i} className={`warehouse-item ${wh.status}`}>
                          <span className="warehouse-name">{wh.warehouse}:</span>
                          <span className="warehouse-qty">{wh.quantity}</span>
                        </div>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function LowStockReport({ data }) {
  const summary = data?.summary || {};
  const items = data?.data || [];

  return (
    <div className="report-section">
      <div className="report-summary-cards">
        <div className="summary-card alert">
          <AlertTriangle size={24} />
          <div>
            <span className="card-label">Total Low Stock Items</span>
            <span className="card-value">{summary.total_items || 0}</span>
          </div>
        </div>
        <div className="summary-card danger">
          <TrendingDown size={24} />
          <div>
            <span className="card-label">Out of Stock</span>
            <span className="card-value">{summary.out_of_stock || 0}</span>
          </div>
        </div>
        <div className="summary-card warning">
          <Package size={24} />
          <div>
            <span className="card-label">Low Stock</span>
            <span className="card-value">{summary.low_stock || 0}</span>
          </div>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="no-data-message">
          <AlertTriangle size={48} />
          <p>No low stock items found</p>
        </div>
      ) : (
        <div className="report-table-container">
          <table className="report-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>SKU</th>
                <th>Variant</th>
                <th>Warehouse</th>
                <th>Current Qty</th>
                <th>Reorder Level</th>
                <th>Shortage</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => (
                <tr key={idx} className={item.current_quantity === 0 ? 'out-of-stock-row' : 'low-stock-row'}>
                  <td className="product-name">{item.product_name}</td>
                  <td className="sku">{item.product_sku}</td>
                  <td>{item.variant || '-'}</td>
                  <td>{item.warehouse}</td>
                  <td className="quantity">{item.current_quantity}</td>
                  <td>{item.reorder_level}</td>
                  <td className="shortage">{item.shortage}</td>
                  <td>
                    <span className={`status-badge ${item.status === 'OUT OF STOCK' ? 'danger' : 'warning'}`}>
                      {item.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function StockValueReport({ data, formatCurrency }) {
  // ✅ FIXED: Add safe defaults and null checks
  const totalValue = data?.summary?.total_value || 0;
  const totalItems = data?.summary?.total_items || 0;
  const warehouseData = data?.data || [];

  // ✅ Show message if no data
  if (warehouseData.length === 0) {
    return (
      <div className="report-section">
        <div className="no-data-message">
          <Package size={48} />
          <p>No stock data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="report-section">
      <div className="report-summary-cards">
        <div className="summary-card">
          <DollarSign size={24} />
          <div>
            <span className="card-label">Total Inventory Value</span>
            <span className="card-value">{formatCurrency(totalValue)}</span>
          </div>
        </div>
        <div className="summary-card">
          <Package size={24} />
          <div>
            <span className="card-label">Total Items</span>
            <span className="card-value">{totalItems}</span>
          </div>
        </div>
      </div>

      <div className="warehouse-value-grid">
        {warehouseData.map((warehouse, idx) => (
          <div key={idx} className="warehouse-value-card">
            <h3>{warehouse.warehouse}</h3>
            <div className="value-stats">
              <div className="stat">
                <span className="stat-label">Total Value</span>
                <span className="stat-value">{formatCurrency(warehouse.total_value || 0)}</span>
              </div>
              <div className="stat">
                <span className="stat-label">Items</span>
                <span className="stat-value">{warehouse.item_count || 0}</span>
              </div>
              <div className="stat">
                <span className="stat-label">% of Total</span>
                <span className="stat-value">
                  {totalValue > 0 ? ((warehouse.total_value / totalValue) * 100).toFixed(1) : 0}%
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function WarehouseComparisonReport({ data, formatCurrency }) {
  const items = data?.data || [];

  if (items.length === 0) {
    return (
      <div className="report-section">
        <div className="no-data-message">
          <BarChart3 size={48} />
          <p>No warehouse data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="report-section">
      <div className="report-table-container">
        <table className="report-table">
          <thead>
            <tr>
              <th>Warehouse</th>
              <th>Location</th>
              <th>Total Items</th>
              <th>Total Quantity</th>
              <th>Low Stock Items</th>
              <th>Total Value</th>
            </tr>
          </thead>
          <tbody>
            {items.map((warehouse, idx) => (
              <tr key={idx}>
                <td className="warehouse-name">{warehouse.warehouse}</td>
                <td>{warehouse.location}</td>
                <td>{warehouse.total_items}</td>
                <td className="quantity">{warehouse.total_quantity}</td>
                <td>
                  {warehouse.low_stock_items > 0 ? (
                    <span className="status-badge warning">
                      {warehouse.low_stock_items}
                    </span>
                  ) : (
                    <span className="status-badge success">0</span>
                  )}
                </td>
                <td className="total-value">{formatCurrency(warehouse.total_value || 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default ReportsPage;
